/**
 * process.sign365.io - orchestration du parcours signataire.
 *
 * Le token vient du chemin de l'URL (/sign/<token>), jamais d'un
 * parametre de requete : les query strings finissent dans les journaux
 * des reverse proxies et des navigateurs, et ce token vaut droit de
 * signer.
 *
 * L'etape courante est DEDUITE de l'etat renvoye par l'API, pas conservee
 * localement : un signataire qui ferme l'onglet entre le consentement et
 * la saisie du code doit reprendre ou il en etait, pas tout recommencer.
 *
 * B-16 (19/09/2026) : ECRAN UNIQUE. Le document ne quitte plus l'ecran -
 * il reste affiche pendant le consentement et pendant la saisie du code,
 * et le panneau d'a cote avance d'une etape a l'autre. Avant, la
 * progression locale ne faisait qu'avancer : passe « Suivant », plus rien
 * ne ramenait au document, pas meme le lien de l'e-mail. Ce n'etait pas
 * qu'une gene : un signataire doit pouvoir consulter ce qu'il signe
 * jusqu'au dernier instant. Le corriger par un bouton « revoir » aurait
 * demande une exception a la regle « on n'avance que » ; le garder a
 * l'ecran supprime la question.
 *
 * Le document et le suivi des pages (B-18) vivent donc ICI, et non dans
 * l'ecran de lecture : ils doivent survivre au changement d'etape.
 */
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";

import { ApiError, CHAMPS_A_SAISIR, CHAMPS_AFFICHES, fetchApercu, fetchDemande, initiales, fetchDocument, fetchView, nomDuRepere, postChamp, type Demande, type SignerView } from "./api";
import { resolveLang, t, type Lang } from "./i18n";
import { ErrorPanel } from "./components/ErrorPanel";
import { Ico } from "./components/Icone";
import { surOrdinateur } from "./components/SurTelephone";
import { Review } from "./screens/Review";
import { Consent } from "./screens/Consent";
import { Signature } from "./screens/Signature";
import { Otp } from "./screens/Otp";
import { Done } from "./screens/Done";
import { Declined } from "./screens/Declined";
import { DeclineForm } from "./screens/DeclineForm";
import { Fin, Menu, Saisie } from "./screens/Parcours";
import { InscriptionCle } from "./screens/CleAcces";
import { Transmettre } from "./screens/Transmettre";
import { EnAttente } from "./screens/EnAttente";
import type { RepereInfo } from "./components/PdfViewer";

// PDF.js pese a lui seul plus que tout le reste de l'application. Charge a
// la demande, il ne retarde pas le premier affichage : l'ecran doit
// repondre en trois secondes a « qui me demande quoi », et cette reponse
// n'a pas besoin de la visionneuse pour s'afficher - le panneau de droite
// la porte.
const PdfViewer = lazy(() =>
  import("./components/PdfViewer").then((m) => ({ default: m.PdfViewer })),
);

// « inscription » (lot 2, 15/10/2026) : signature par clé sans compte ET clé
// - compte, PVID et clé AVANT le document. « transmitting » : la signature
// passe à une autre personne.
// « attente » (lot 2bis) : une demande attend la reponse de l'emetteur.
type Step = "inscription" | "review" | "consent" | "signature" | "otp" | "done" | "declined" | "declining"
  | "transmitting" | "attente" | "closed";

function tokenFromUrl(): string | null {
  const match = window.location.pathname.match(/\/sign\/([^/?#]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export default function App() {
  const [token] = useState(tokenFromUrl);
  const [view, setView] = useState<SignerView | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  // Progression locale, qui ne fait qu'AVANCER par rapport a l'etat
  // serveur - jamais reculer. Elle sert aux transitions immediates
  // (consentement enregistre) sans attendre un rechargement complet.
  // Depuis B-16 cela ne prive plus de rien : le document reste a l'ecran
  // a toutes les etapes.
  const [advanced, setAdvanced] = useState<Step | null>(null);
  // Lot 2bis : lien expire parce qu'une transmission demandee a ete
  // acceptee - on dit « transmise », pas « lien expire ».
  const [transmise, setTransmise] = useState<Demande | null>(null);

  // Le document, charge une seule fois pour tout le parcours.
  const [pdf, setPdf] = useState<ArrayBuffer | null>(null);
  const [pdfError, setPdfError] = useState<ApiError | null>(null);

  // B-18 : ce qui a ete mis sous les yeux du signataire. `pagesVues` ne
  // contient que des pages dont le rendu a abouti (cf. PdfViewer).
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [pagesVues, setPagesVues] = useState<number[]>([]);
  const [viewerFailed, setViewerFailed] = useState(false);
  // B-23 / B-28 : SES emplacements, lus dans le PDF par la visionneuse.
  const [reperes, setReperes] = useState<RepereInfo[]>([]);
  const nbReperes = reperes.length;
  const [prochainRepere, setProchainRepere] = useState(0);
  // B-28 : cadres date / nom / e-mail valides d'un clic (le cadre de
  // signature, lui, est fait quand le visuel est choisi).
  const [valides, setValides] = useState<ReadonlySet<number>>(new Set());
  const [zoom, setZoom] = useState(1);
  const [choixSignature, setChoixSignature] = useState(false);
  // Change a chaque nouveau visuel : les apercus se rechargent.
  const [versionVisuel, setVersionVisuel] = useState(0);
  // B-23 : l'apercu suit le visuel CHOISI ; avant ce choix, cadres vides.
  const chargerApercu = useCallback(
    (kind: string, w: number, h: number) => fetchApercu(token ?? "", kind, w, h),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, versionVisuel]);

  const lang: Lang = useMemo(() => resolveLang(view?.langue), [view?.langue]);
  // B-13 : l'approbateur a ses propres textes (aucun mot « signature »).
  const L = useMemo(() => t(lang, view?.role), [lang, view?.role]);

  const load = useCallback(async () => {
    if (!token) {
      setError(new ApiError("not_found", 404, null));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setView(await fetchView(token));
      setError(null);
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError("server", 0, null);
      if (err.kind === "expired") {
        const d = await fetchDemande(token).catch(() => null);
        if (d && d.type === "transmission" && d.statut === "acceptee") setTransmise(d);
      }
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchDocument(token);
        if (!cancelled) setPdf(data);
      } catch (e) {
        // Le document n'a pas pu etre RECUPERE. Ce n'est pas le cas du
        // repli par declaration : la, le signataire a les octets et peut
        // les telecharger. Ici il n'a rien, et lui proposer de declarer
        // avoir pris connaissance d'un document qu'il ne peut pas ouvrir
        // serait recueillir une declaration vide.
        if (!cancelled) setPdfError(e instanceof ApiError ? e : new ApiError("server", 0, null));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    document.documentElement.lang = lang;
    if (view?.issuer_name) {
      // Le titre d'onglet ne doit jamais reveler le contenu du document a
      // quelqu'un qui regarde l'ecran par-dessus l'epaule : on nomme
      // l'emetteur, pas le document.
      document.title = `${view.issuer_name} — ${L.onglet}`;
    }
  }, [lang, view?.issuer_name, L.onglet]);

  const marquerVue = useCallback((numero: number) => {
    setPagesVues((vues) => (vues.includes(numero) ? vues : [...vues, numero]));
  }, []);

  /**
   * Aller a une page, ET l'amener sous les yeux.
   *
   * Sur telephone les deux colonnes s'empilent : le panneau est SOUS le
   * document, et « aller a la premiere page non affichee » changeait une
   * page situee hors de l'ecran. Rien ne bougeait la ou le doigt venait
   * d'appuyer - le bouton avait l'air cassé. Sur grand ecran le document
   * est deja visible, et un defilement automatique serait une secousse
   * gratuite : on ne remonte que si la visionneuse n'est pas a l'ecran.
   */
  const allerALaPage = useCallback((numero: number) => {
    setPage(numero);
    const pane = document.getElementById("document-a-signer");
    if (!pane) return;
    const haut = pane.getBoundingClientRect().top;
    if (haut < 0 || haut > window.innerHeight * 0.5) {
      pane.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  /** Telechargement et impression passent par un blob local : le document
   *  ne quitte jamais le navigateur pour un service tiers. */
  const withBlob = useCallback(
    (action: (url: string) => void) => {
      if (!pdf) return;
      const url = URL.createObjectURL(new Blob([pdf.slice(0)], { type: "application/pdf" }));
      action(url);
      // Revocation differee : revoquer immediatement annulerait le
      // telechargement ou l'impression avant qu'ils n'aient commence.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    [pdf],
  );

  const download = useCallback(() => {
    withBlob((url) => {
      const a = document.createElement("a");
      a.href = url;
      // Noms du 17/09/2026 : avant la fin, jamais confondu avec le document final.
      a.download = `${view?.title || "document"} - en cours de signature.pdf`;
      a.click();
    });
  }, [withBlob, view?.title]);

  const print = useCallback(() => {
    withBlob((url) => {
      const frame = document.createElement("iframe");
      frame.style.display = "none";
      frame.src = url;
      frame.onload = () => frame.contentWindow?.print();
      document.body.appendChild(frame);
    });
  }, [withBlob]);

  // Lot 3bis : le cadre du serveur derriere un rang (champs a saisir).
  const cadreDe = (i: number) => view?.emplacements.find((e) => e.id === reperes[i]?.id);
  const faits = useMemo(() => new Set(reperes.flatMap((r, i) => {
    // Date, nom, e-mail : affiches, rien a faire - donc jamais « restants ».
    if (CHAMPS_AFFICHES.includes(r.kind)) return [i];
    if (CHAMPS_A_SAISIR.includes(r.kind)) {
      // Fait = saisi (en base, donc garde au rechargement) ; un champ
      // facultatif laisse vide compte des qu'on l'a vu et valide.
      const e = view?.emplacements.find((x) => x.id === r.id);
      if (e?.kind === "radio") {
        // Un groupe est fait des qu'une de ses options est choisie.
        const choisi = view?.emplacements.some((x) => x.kind === "radio" && x.groupe === e.groupe && x.valeur);
        return choisi || (!e.obligatoire && valides.has(i)) ? [i] : [];
      }
      return e && (e.valeur || (!e.obligatoire && valides.has(i))) ? [i] : [];
    }
    return (valides.has(i) && (r.kind !== "signature" || Boolean(view?.signature_methode))) ? [i] : [];
  })), [reperes, valides, view?.signature_methode, view?.emplacements]);
  const valeursSaisies = useMemo(() => Object.fromEntries(reperes.flatMap((r, i) => {
    const e = view?.emplacements.find((x) => x.id === r.id);
    if (r.kind === "texte_fixe" && e?.libelle) return [[i, e.libelle]];
    if (!e?.valeur || !CHAMPS_A_SAISIR.includes(r.kind)) return [];
    const affiche = r.kind === "case" ? "X" : r.kind === "radio" ? "●"
      : r.kind === "paraphe" ? initiales(view?.signer_name || "") : e.valeur;
    return [[i, affiche]];
  })), [reperes, view?.emplacements, view?.signer_name]);
  // B-28 (porteur, 11/10/2026) : tous ses paraphes en un clic, chacun
  // enregistre comme s'il l'avait clique.
  const paraphesVides = (view?.emplacements ?? []).filter((e) => e.kind === "paraphe" && !e.valeur);
  // Porteur, 12/10/2026 : l'emetteur a pu exiger un clic sur CHAQUE paraphe
  // (groupe « chaque_page ») ; sinon un clic les valide tous.
  const paraphesUnParUn = (view?.emplacements ?? []).some((e) => e.kind === "paraphe" && e.groupe === "chaque_page");
  const parapherTout = async () => {
    for (const e of paraphesVides) await postChamp(token ?? "", e.id, "oui");
    await load();
  };
  const [saisie, setSaisie] = useState<number | null>(null);
  const [accueilVu, setAccueilVu] = useState(false);
  // Le cadre de signature qui a ouvert le choix du visuel : il sera coche
  // une fois le visuel enregistre.
  const [repereEnAttente, setRepereEnAttente] = useState<number | null>(null);
  // Franck, 21/10/2026 : « defilement des pages jusqu'a la derniere page » -
  // CHAQUE page doit avoir ete vue (le serveur l'exige aussi). Une lecture
  // deja enregistree (retour sur le lien) ne se refait pas.
  const nonVues = Array.from({ length: pageCount }, (_, i) => i + 1).filter((n) => !pagesVues.includes(n));
  const toutesVues = Boolean(view?.reviewed) || (pageCount > 0 && nonVues.length === 0);
  const allerAuRepere = (i: number) =>
    document.getElementById(`repere-${i}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
  const suivant = () => {
    const i = reperes.findIndex((_, n) => !faits.has(n));
    if (i >= 0) allerAuRepere(i);
  };
  const versLaFin = () =>
    document.getElementById("fin-du-document")?.scrollIntoView({ block: "start", behavior: "smooth" });
  const clicRepere = (i: number) => {
    const r = reperes[i];
    if (r && CHAMPS_A_SAISIR.includes(r.kind)) {
      const e = cadreDe(i);
      if (!e) return;
      if (r.kind === "paraphe" && !paraphesUnParUn && !e.valeur && paraphesVides.length > 1) {
        void parapherTout();
        return;
      }
      if (r.kind === "case" || r.kind === "paraphe" || r.kind === "radio") {
        // Case, paraphe, option : un clic, enregistre aussitot. Un second
        // clic annule (le serveur decoche les autres options du groupe).
        void postChamp(token ?? "", e.id, e.valeur ? null : "oui").then(() => {
          setValides((v) => new Set(v).add(i));
          return load();
        });
      } else {
        setSaisie(i);
      }
      return;
    }
    if (reperes[i]?.kind === "signature" && !view?.signature_methode) {
      setRepereEnAttente(i);
      setChoixSignature(true);
      return;
    }
    // Un second clic annule : on doit pouvoir revenir sur une validation.
    setValides((v) => {
      const n = new Set(v);
      if (n.has(i)) n.delete(i); else n.add(i);
      return n;
    });
  };

  const step: Step = useMemo(() => {
    if (!view) return "review";
    if (view.declined) return "declined";
    // 06/10/2026 : annulee par l'emetteur, refusee par un autre, expiree.
    // Avant, le parcours s'affichait et la premiere action butait sur un
    // 409 « etape deja effectuee ».
    if (view.envelope_status && view.envelope_status in L.closedTitle) return "closed";
    // « authenticated » : le bon code est saisi, le scellement suit. Pour le
    // signataire, c'est signe - et le serveur refuse desormais toute autre
    // action sur ce lien (PS-2). Sans cette ligne, recharger la page pendant
    // le scellement renvoyait a la saisie d'un nouveau code.
    if (view.signed || view.signer_status === "authenticated") return "done";
    if (advanced === "declining") return "declining";
    if (view.demande?.statut === "en_attente" || advanced === "attente") return "attente";
    if (advanced === "transmitting") return "transmitting";
    if (advanced === "done") return "done";
    // Lot 2 : le serveur refuse lecture et consentement avant la clé (428).
    if (view.cle_acces && !view.cle_prete) return "inscription";
    // B-17 : le visuel de signature s'intercale entre le consentement et le
    // code. L'acte juridique reste la validation du code ; cette etape ne
    // fait que choisir comment la signature se montrera.
    // B-13 : l'approbateur ne choisit pas de signature, il passe au code.
    if (view.signature_methode || advanced === "otp" || (view.role === "approver" && view.consent_given)) return "otp";
    if (view.consent_given || advanced === "signature") return "signature";
    // B-18 : lecture deja enregistree cote serveur - le signataire qui
    // revient sur son lien ne refait pas defiler tout le document.
    if (view.reviewed || advanced === "consent") return "consent";
    return "review";
  }, [view, advanced, L]);

  if (loading && !view) {
    return (
      <div className="centered">
        <div className="spinner" role="status" aria-label={L.loading} />
        <p className="muted">{L.loading}</p>
      </div>
    );
  }

  if (transmise && !view && token) {
    return (
      <Shell issuer={null} L={L}>
        <EnAttente token={token} L={L} client="" demande={transmise} onReprendre={() => undefined} />
      </Shell>
    );
  }

  if (error && !view) {
    return (
      <Shell issuer={null} L={L}>
        <div className="card">
          <ErrorPanel error={error} L={L} onRetry={error.kind === "network" ? load : undefined} />
        </div>
      </Shell>
    );
  }

  if (!view || !token) return null;

  // Ces trois etapes n'ont plus rien a lire : le document a ete signe,
  // refuse, ou le signataire est en train de motiver son refus. La
  // visionneuse leur volerait la moitie de l'ecran pour rien.
  const sansDocument = step === "done" || step === "declined" || step === "declining" || step === "closed"
    || step === "inscription" || step === "transmitting" || step === "attente";
  // Franck, 20/10/2026 : transmission interdite (reglage « interdire » ou
  // demande deja refusee par l'emetteur) = RIEN a l'ecran, ni lien grise ni
  // motif. Le serveur continue de refuser.
  const versTransmission = view.transmission === "interdire" || view.transmission_refusee
    ? undefined : () => setAdvanced("transmitting");
  const reprendre = () => {
    setAdvanced(null);
    void load();
  };
  // B-23 : le visuel est choisi (etape du code) - on montre ce qui sera pose.
  const avecApercu = step === "otp";

  if (sansDocument) {
    return (
      <Shell issuer={view.issuer_name} L={L}>
        {step === "done" && <Done token={token} L={L} />}
        {step === "declining" && (
          <DeclineForm
            token={token}
            L={L}
            onDeclined={() => {
              setAdvanced(null);
              void load();
            }}
            onCancel={() => setAdvanced(null)}
          />
        )}
        {step === "inscription" && (
          <InscriptionCle token={token} transmettre={L.transmettre} onPret={() => void load()} onRecharger={reprendre}
                          onDecline={() => setAdvanced("declining")} refuser={L.refuse} emetteur={view.issuer_name}
                          onTransmettre={versTransmission} />
        )}
        {step === "transmitting" && (
          <Transmettre token={token} view={view} L={L} onCancel={() => setAdvanced(null)} onDemandee={reprendre} />
        )}
        {step === "attente" && view.demande && (
          <EnAttente token={token} L={L} client={view.issuer_name ?? ""} demande={view.demande}
                     onReprendre={reprendre} />
        )}
        {step === "declined" && <Declined L={L} />}
        {step === "closed" && (
          <Declined L={L} titre={L.closedTitle[view.envelope_status ?? ""]} corps={L.closedBody} />
        )}
      </Shell>
    );
  }

  if (!viewerFailed && !pdfError) {
    return (
      <Shell issuer={view.issuer_name} L={L} large sansBandeau>
        <div className="parcours">
          <div className="parcours__document" id="document-a-signer">
            <h1 className="parcours__titre-mobile">{view.title}</h1>
            {nbReperes > faits.size && (
              <div className="parcours__suivant">
                <button type="button" className="btn btn--primary" onClick={suivant}>
                  {(() => {
                    const i = reperes.findIndex((_, n) => !faits.has(n));
                    return L.prochainAValider(nomDuRepere(L.reperes, reperes[i]!), reperes[i]!.page);
                  })()}
                </button>
              </div>
            )}
            {pdf ? (
              <Suspense fallback={<div className="centered" style={{ minHeight: "30vh" }}>
                <div className="spinner" role="status" aria-label={L.loading} /></div>}>
                <PdfViewer
                  data={pdf}
                  labels={L}
                  page={page}
                  onPageChange={setPage}
                  onPageCount={setPageCount}
                  onPageShown={marquerVue}
                  onFailed={() => setViewerFailed(true)}
                  onDownload={download}
                  onPrint={print}
                  emplacements={view.emplacements}
                  libellesReperes={{ ...L.reperes, signature: view.signer_name ? `Signature ${view.signer_name}` : L.reperes.signature }}
                  onReperes={setReperes}
                  onRepereClic={clicRepere}
                  reperesFaits={faits}
                  chargerApercu={chargerApercu}
                  avecSignature={Boolean(view.signature_methode)}
                  pagesMasquees={view.pages_annexe}
                  valeursSaisies={valeursSaisies}
                  toujoursFlux
                  sansBarre
                  zoom={zoom}
                />
              </Suspense>
            ) : (
              <div className="centered" style={{ minHeight: "30vh" }}>
                <div className="spinner" role="status" aria-label={L.loading} />
              </div>
            )}
            <Fin
              token={token}
              view={view}
              L={L}
              pagesVues={pagesVues}
              toutesVues={toutesVues}
              pageCount={pageCount}
              premiereNonVue={nonVues[0]}
              onAllerALaPage={allerALaPage}
              restants={nbReperes - faits.size}
              onSuivant={suivant}
              onDecline={() => setAdvanced("declining")}
              onSigned={() => setAdvanced("done")}
              onRecharger={() => void load()}
              onChoisirSignature={() => setChoixSignature(true)}
              onTransmettre={versTransmission}
            />
          </div>
          <Menu
            view={view}
            L={L}
            reperes={reperes}
            faits={faits}
            pageCount={pageCount}
            page={page}
            zoom={zoom}
            onZoom={setZoom}
            onPage={allerALaPage}
            onAller={allerAuRepere}
            onFin={() => (toutesVues ? versLaFin() : allerALaPage(nonVues[0] ?? pageCount))}
            onDownload={download}
            onPrint={print}
            paraphesRestants={paraphesUnParUn ? 0 : paraphesVides.length}
            onParapherTout={() => void parapherTout()}
          />
        </div>
        {!accueilVu && !view.consent_given && (
          <div className="dialogue" role="dialog" aria-modal="true" aria-labelledby="accueil-titre">
            <div className="dialogue__corps accueil">
              <div className="accueil__emetteur">
                <span className="accueil__pastille" aria-hidden="true">
                  {(view.issuer_name ?? "?").split(/\s+/).filter(Boolean).slice(0, 2).map((m) => m[0]).join("").toUpperCase()}
                </span>
                <span>
                  <strong>{view.issuer_name}</strong>
                  <span className="accueil__action"> {L.asksYouToSign}</span>
                </span>
              </div>
              <h2 id="accueil-titre" className="accueil__titre">{view.title}</h2>
              {(view.message_subject || view.message_body) && (
                <blockquote className="accueil__message">
                  {view.message_subject && <p className="accueil__objet">{view.message_subject}</p>}
                  {view.message_body && <p>{view.message_body}</p>}
                </blockquote>
              )}
              <p className="accueil__aide">{view.cle_acces ? L.accueilAideCle : L.accueilAide}</p>
              {view.cle_acces && surOrdinateur() && (
                <p className="note-telephone"><Ico nom="telephone" /> {L.accueilCleOrdinateur}</p>
              )}
              <div className="accueil__actions">
                <button type="button" className="btn btn--refus" onClick={() => { setAccueilVu(true); setAdvanced("declining"); }}>
                  {L.refuserDeSigner}
                </button>
                <button type="button" className="btn btn--primary" autoFocus onClick={() => setAccueilVu(true)}>
                  {L.lireEtSigner}
                </button>
              </div>
              {versTransmission && (
                <button type="button" className="btn btn--discret accueil__transmettre"
                        onClick={() => { setAccueilVu(true); versTransmission(); }}>{L.transmettre}</button>
              )}
            </div>
          </div>
        )}
        {saisie !== null && cadreDe(saisie) && (
          <div className="dialogue" role="dialog" aria-modal="true">
            <div className="dialogue__corps">
              <Saisie
                token={token ?? ""}
                cadre={cadreDe(saisie)!}
                L={L}
                onAnnuler={() => setSaisie(null)}
                onFait={() => {
                  const i = saisie;
                  setValides((v) => new Set(v).add(i));
                  setSaisie(null);
                  void load();
                }}
              />
            </div>
          </div>
        )}
        {choixSignature && (
          <div className="dialogue" role="dialog" aria-modal="true">
            <div className="dialogue__corps">
              <button type="button" className="icon-btn dialogue__fermer" aria-label={L.fermer}
                      onClick={() => setChoixSignature(false)}>×</button>
              <Signature
                token={token}
                view={view}
                L={L}
                onDone={() => {
                  setChoixSignature(false);
                  if (repereEnAttente !== null) {
                    const i = repereEnAttente;
                    setValides((v) => new Set(v).add(i));
                    setRepereEnAttente(null);
                  }
                  setVersionVisuel((v) => v + 1);
                  void load();
                }}
                // Franck, 20/10/2026 : dans la fenetre, « Annuler » la ferme (pas un refus).
                onAnnuler={() => setChoixSignature(false)}
              />
            </div>
          </div>
        )}
      </Shell>
    );
  }

  return (
    <Shell issuer={view.issuer_name} L={L} large>
      {/* Visionneuse en echec : la colonne de gauche n'a plus rien a
          montrer. La garder afficherait une seconde fois le meme message
          et le meme bouton de telechargement que le panneau, a cote de la
          case a cocher - deux boutons identiques pour une seule action. */}
      <div className={viewerFailed ? "split split--seul" : "split"}>
        <section className="split__document card" id="document-a-signer" hidden={viewerFailed}>
          {pdfError ? (
            <ErrorPanel error={pdfError} L={L} />
          ) : pdf ? (
            <>
              {nbReperes > 0 && !viewerFailed && (
                <div className="reperes-annonce">
                  <p style={{ margin: 0 }}>{L.reperesCompte(nbReperes)}</p>
                </div>
              )}
            <Suspense
              fallback={
                <div className="centered" style={{ minHeight: "30vh" }}>
                  <div className="spinner" role="status" aria-label={L.loading} />
                </div>
              }
            >
              <PdfViewer
                data={pdf}
                labels={L}
                page={page}
                onPageChange={setPage}
                onPageCount={setPageCount}
                onPageShown={marquerVue}
                onFailed={() => setViewerFailed(true)}
                onDownload={download}
                onPrint={print}
                emplacements={view.emplacements}
                libellesReperes={{ ...L.reperes, signature: view.signer_name ? `Signature ${view.signer_name}` : L.reperes.signature }}
                onReperes={setReperes}
                chargerApercu={avecApercu ? chargerApercu : undefined}
                barreExtra={nbReperes > 0 && (
                  <button
                    type="button"
                    className="btn btn--secondary repere-suivant"
                    aria-label={L.reperesSuivant(prochainRepere + 1, nbReperes)}
                    onClick={() => {
                      document.getElementById(`repere-${prochainRepere}`)
                        ?.scrollIntoView({ block: "center" });
                      setProchainRepere((i) => (i + 1) % nbReperes);
                    }}
                  >
                    {L.reperesCourt(prochainRepere + 1, nbReperes)}
                  </button>
                )}
              />
            </Suspense>
            </>
          ) : (
            <div className="centered" style={{ minHeight: "30vh" }}>
              <div className="spinner" role="status" aria-label={L.loading} />
            </div>
          )}
        </section>

        <div className="split__panneau">
          {step === "review" && (
            <Review
              token={token}
              view={view}
              L={L}
              pagesVues={pagesVues}
              pageCount={pageCount}
              viewerFailed={viewerFailed}
              documentPret={pdf !== null || viewerFailed}
              onAllerALaPage={allerALaPage}
              onDownload={download}
              onDone={() => {
                setAdvanced("consent");
                void load();
              }}
              onDecline={() => setAdvanced("declining")}
            />
          )}

          {step === "consent" && (
            <Consent
              token={token}
              L={L}
              consentement={view?.consentement}
              onDone={() => {
                setAdvanced("signature");
                void load();
              }}
              onDecline={() => setAdvanced("declining")}
            />
          )}

          {step === "signature" && (
            <Signature
              token={token}
              view={view}
              L={L}
              onDone={() => {
                setAdvanced("otp");
                void load();
              }}
              onDecline={() => setAdvanced("declining")}
            />
          )}

          {step === "otp" && (
            <Otp
              token={token}
              view={view}
              L={L}
              onSigned={() => setAdvanced("done")}
              onDecline={() => setAdvanced("declining")}
            />
          )}
        </div>
      </div>
    </Shell>
  );
}

function Shell({
  issuer,
  L,
  large,
  sansBandeau,
  children,
}: {
  issuer: string | null;
  L: ReturnType<typeof t>;
  large?: boolean;
  /** B-28 : le bandeau bleu est retire ; l'emetteur passe en tete du menu. */
  sansBandeau?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="page">
      <header className="masthead" hidden={sansBandeau}>
        <div className="masthead__inner">
          {issuer ? (
            <>
              <span className="masthead__issuer">{issuer}</span>
              <span className="masthead__action">{L.asksYouToSign}</span>
            </>
          ) : (
            <span className="masthead__issuer">Signature électronique</span>
          )}
        </div>
      </header>
      <main className={large ? "shell shell--large" : "shell"}>{children}</main>
      <footer className="footer">{L.needHelp}</footer>
    </div>
  );
}
